import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useI18n, type Lang } from "@/lib/i18n";
import { PageShell } from "@/components/site/PageShell";
import { CATEGORIES } from "@/lib/categories";
import {
  storyBySlugQuery,
  reactionsTotalsQuery,
  categoriesAllQuery,
  formatLogDate,
} from "@/lib/stories";

export const Route = createFileRoute("/$lang/stories/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: "Entry — MadCaptains" },
      { name: "description", content: "Anonymous testimony from the merchant fleet." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `/${params.lang}/stories/${params.slug}` },
    ],
    links: [{ rel: "canonical", href: `/${params.lang}/stories/${params.slug}` }],
  }),
  loader: async ({ context, params }) => {
    const story = await context.queryClient.ensureQueryData(storyBySlugQuery(params.slug));
    if (!story) throw notFound();
    // Prime reactions + categories in parallel.
    await Promise.all([
      context.queryClient.ensureQueryData(reactionsTotalsQuery(story.id)),
      context.queryClient.ensureQueryData(categoriesAllQuery()),
    ]);
    return { story };
  },
  component: StoryPage,
  errorComponent: ({ error }) => (
    <PageShell title="Could not load this entry">
      <p className="text-sm text-muted-foreground">{error.message}</p>
    </PageShell>
  ),
  notFoundComponent: NotFoundEntry,
});

function NotFoundEntry() {
  const { lang, t } = useI18n();
  return (
    <PageShell title={t.not_found_h1} lead={t.not_found_lead}>
      <Link
        to="/$lang/stories"
        params={{ lang }}
        className="text-sm underline decoration-dotted underline-offset-4"
      >
        {t.not_found_back}
      </Link>
    </PageShell>
  );
}

function StoryPage() {
  const { slug } = Route.useParams();
  const { lang, t } = useI18n();
  const { data: story } = useSuspenseQuery(storyBySlugQuery(slug));
  const { data: cats } = useSuspenseQuery(categoriesAllQuery());
  const { data: reactions } = useSuspenseQuery(
    reactionsTotalsQuery(story?.id ?? "00000000-0000-0000-0000-000000000000"),
  );

  if (!story) return <NotFoundEntry />;

  const idToSlug = new Map(cats.map((c) => [c.id, c.slug]));
  const cwList = story.content_warnings ?? [];
  const storyCats = (story.category_ids ?? [])
    .map((id) => {
      const sl = idToSlug.get(id);
      const c = sl ? CATEGORIES.find((x) => x.slug === sl) : undefined;
      return c ? { slug: c.slug, flag: c.flag, label: lang === "ru" ? c.label.ru : c.label.en } : null;
    })
    .filter(Boolean) as { slug: string; flag: string; label: string }[];

  return (
    <article className="mx-auto max-w-[44rem] px-5 py-16 md:py-20">
      <p className="mb-4 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        Logbook entry · {formatLogDate(story.published_at, lang as Lang)}
      </p>
      <h1 className="font-display text-3xl leading-[1.1] sm:text-4xl">
        {story.title ?? "Untitled entry"}
      </h1>
      {story.title_original && story.title_original !== story.title && (
        <p className="mt-2 font-display italic text-muted-foreground">{story.title_original}</p>
      )}

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        {story.author_display && <span>{story.author_display}</span>}
        <span>· Language {story.language.toUpperCase()}</span>
      </div>

      {cwList.length > 0 && (
        <aside
          className="mt-8 rounded-sm border-l-2 px-4 py-3 text-sm"
          style={{ borderColor: "var(--rust)", background: "color-mix(in oklab, var(--rust) 8%, transparent)" }}
        >
          <p className="font-display italic">{lang === "ru" ? "Предупреждение о содержании" : "Content warning"}</p>
          <p className="mt-1 text-muted-foreground">{cwList.join(" · ")}</p>
        </aside>
      )}

      <div className="prose prose-invert mt-10 max-w-none font-display text-lg leading-[1.7] [&_p]:mb-5 whitespace-pre-wrap">
        {story.body}
      </div>

      {storyCats.length > 0 && (
        <div className="mt-12 border-t border-border pt-6">
          <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            {t.page_categories_h1}
          </p>
          <ul className="flex flex-wrap gap-2">
            {storyCats.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/$lang/categories/$slug"
                  params={{ lang, slug: c.slug }}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs hover:bg-card/50"
                >
                  <span className="font-display font-semibold">{c.flag.toUpperCase()}</span>
                  <span>{c.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ReactionBar storyId={story.id} totals={reactions} lang={lang} />

      <div className="mt-12 border-t border-border pt-6">
        <Link
          to="/$lang/stories"
          params={{ lang }}
          className="text-sm underline decoration-dotted underline-offset-4"
        >
          ← {t.page_stories_h1}
        </Link>
      </div>
    </article>
  );
}

type ReactionKey = "same_happened_to_me" | "support" | "thank_you";

function ReactionBar({
  storyId,
  totals,
  lang,
}: {
  storyId: string;
  totals: { same_happened_to_me: number; support: number; thank_you: number };
  lang: Lang;
}) {
  const qc = useQueryClient();
  const storageKey = `mc:reacted:${storyId}`;
  const [reacted, setReacted] = useState<Set<ReactionKey>>(new Set());

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) setReacted(new Set(JSON.parse(raw) as ReactionKey[]));
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const mutation = useMutation({
    mutationFn: async (type: ReactionKey) => {
      const { data, error } = await supabase
        .from("reactions")
        .insert({ story_id: storyId, type })
        .select()
        .single();
      if (error) {
        console.error("[reactions] insert failed", { storyId, type, error });
        throw new Error(error.message);
      }
      console.info("[reactions] inserted", data);
      return type;
    },
    onError: (err, type) => {
      console.error("[reactions] mutation error", { type, err });
    },
    onSuccess: async (type) => {
      setReacted((prev) => {
        const next = new Set(prev);
        next.add(type);
        try {
          sessionStorage.setItem(storageKey, JSON.stringify([...next]));
        } catch {
          /* ignore */
        }
        return next;
      });
      await qc.invalidateQueries({ queryKey: ["reactions", "totals", storyId] });
      await qc.refetchQueries({ queryKey: ["reactions", "totals", storyId] });
    },
  });

  const labels: Record<ReactionKey, string> = {
    same_happened_to_me: lang === "ru" ? "со мной так же" : "same happened to me",
    support: lang === "ru" ? "поддержка" : "support",
    thank_you: lang === "ru" ? "спасибо" : "thank you",
  };

  const items: { key: ReactionKey; count: number }[] = [
    { key: "same_happened_to_me", count: totals.same_happened_to_me },
    { key: "support", count: totals.support },
    { key: "thank_you", count: totals.thank_you },
  ];

  return (
    <div className="mt-10">
      <p className="mb-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        {lang === "ru" ? "Реакции" : "Reactions"}
      </p>
      <div className="flex flex-wrap gap-2">
        {items.map(({ key, count }) => {
          const done = reacted.has(key);
          const pending = mutation.isPending && mutation.variables === key;
          // Show the server count; `done` only controls disabled state.
          // While pending, show +1 optimistically.
          const display = count + (pending ? 1 : 0);
          return (
            <button
              key={key}
              type="button"
              disabled={done || pending}
              onClick={() => mutation.mutate(key)}
              className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs transition-colors hover:bg-card/50 disabled:cursor-not-allowed disabled:opacity-60"
              aria-pressed={done}
            >
              <span>{labels[key]}</span>
              <span className="font-display tabular-nums text-muted-foreground">
                {display}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}